<template>
	<v-container class="evaluation">
		<v-layout>
			<v-btn @click="openProjectView" class="no-print">
				Podgląd wniosku
			</v-btn>
			<v-spacer></v-spacer>
			<v-btn class="mr-3 no-print" @click="print">Wydrukuj</v-btn>
		</v-layout>

		<v-card-title class="mt-3 mb-3">
			<div>
				<h2 class="mb-3">Opinia merytoryczna</h2>
				<div class="evaluation-card evaluation-font-size">
					<div>
						<strong class="mr-3">Nazwa placówki:</strong> {{viewData.agencyName}}
					</div>
					<div>
						<strong class="mr-3">Nazwa projektu:</strong> {{viewData.agencyProjectTitle}}
					</div>
					<div>
						<strong class="mr-3">Numer wniosku:</strong> {{viewData.agencyProjectCode}}
					</div>
					<div>
						<strong class="mr-3">Suma kontrolna:</strong> {{viewData.controlSum}}
					</div>
					<div class="mt-3">
						<strong class="mr-3">Wnioskowana kwota:</strong> {{formatMoney(viewData.mszFundings)}} {{viewData.currencyCode}}
					</div>
					<div class="mt-3">
						<strong class="mr-3">Kraj:</strong> {{viewData.country}}
					</div>
					<div v-if="viewData.extraCountries.length > 0">
						<strong class="mr-3">Kraje dodatkowe:</strong> {{viewData.extraCountries.join(', ')}}
					</div>
					<div v-if="viewData.author">
						<strong class="mr-3">Autor:</strong> {{viewData.author}}
					</div>
					<div v-if="viewData.firstSendDate">
						<strong class="mr-3">Data pierwszego wysłania:</strong> {{viewData.firstSendDate}}
					</div>
					<div v-if="viewData.latestSendDate">
						<strong class="mr-3">Data ostatniego wysłania:</strong> {{viewData.latestSendDate}}
					</div>
				</div>
			</div>
		</v-card-title>

		<hr class="ma-4">

		<vue-form-generator
			name="generator"
			ref="vfg"
			:model="evaluationData.model"
			:options="formGenerator.formOptions"
			:schema="formGenerator.currentSchema"
			v-if="viewData.showGenerator">
		</vue-form-generator>

		<hr class="ma-4">

		<v-form ref="form" lazy-validation v-if="!configuration.booleanEvaluation" class="mb-5">
			<h3 class="mt-4 mb-4 ml-4">Ocena punktowa</h3>
			<div class="no-print">
				<v-container v-for="parameter in configuration.parameters" v-bind:key="parameter.id"
					class="text-xs-center ml-4">
					<v-layout row align-center>
						<v-flex xs4>
							<v-text-field
								browser-autocomplete="off"
								:data-vv-name="'param' + parameter.id"
								:data-vv-as="parameter.name"
								:error-messages="vErrors.collect('param' + parameter.id)"
								v-validate="{ numeric: true, required: true, between: [parameter.minRating, parameter.maxRating] }"
								:min="parameter.minRating"
								:max="parameter.maxRating"
								data-vv-validate-on="change"
								:name="parameter.id"
								:disabled="configuration.disabled"
								type="number"
								v-model="parameter.value"
								:hint="'Od '.concat(parameter.minRating).concat(' do ').concat(parameter.maxRating).concat(' pkt z mnożnikiem ').concat(parameter.multiplier)"
								:label="parameter.name" persistent-hint
								aria-label="evaluation">
							</v-text-field>
						</v-flex>
						<h3 class="ml-5"> Punkty: {{ (parameter.value * parameter.multiplier).toFixed(2) }} </h3>
					</v-layout>
				</v-container>
				<v-layout row align-center>
					<v-flex xs4/>
					<h3 class="mt-4">Suma punktów: {{sumOfPoints.toFixed(2)}}</h3>
				</v-layout>
			</div>
			<div class="d-none print-only ml-3 header-card-font-size">
				<v-container v-for="parameter in configuration.parameters" v-bind:key="parameter.id" class="ml-1">
					<v-layout row>
						<v-flex xs8 class="header-card-font-size text-xs-left">{{parameter.name +
							" (Od ".concat(parameter.minRating).concat(" do ").concat(parameter.maxRating)
								.concat(" pkt z mnożnikiem ").concat(parameter.multiplier) + "):"}}<strong
							class="header-card-font-size ml-4">{{parameter.value}}</strong></v-flex>
						<v-flex xs3 class="header-card-font-size text-xs-right">Punkty: <strong
							class="header-card-font-size ml-1">{{ (parameter.value * parameter.multiplier).toFixed(2) }}</strong>
						</v-flex>
					</v-layout>
				</v-container>
				<v-layout row mt-3 ml-1>
					<v-flex xs8 class="header-card-font-size text-xs-left"/>
					<v-flex xs3 class="text-xs-right"><strong class="header-card-font-size">Suma punktów:
						{{sumOfPoints.toFixed(2)}}</strong></v-flex>
				</v-layout>
			</div>
		</v-form>

		<v-form v-if="configuration.booleanEvaluation">
			<div style="page-break-inside: avoid">
				<h3 class="mt-4 mb-4 ml-4">Rekomendacja</h3>
				<v-container class="no-print">
					<v-layout row class="mb-4 evaluation-recommendation-outer">
						<v-flex xs1/>
						<v-flex xs10 :style="recommendationFrameStyle" class="evaluation-recommendation">
							<v-select
								class="ma-3"
								data-vv-name="recommending"
								data-vv-as="Rekomenduję do dofinansowania"
								:error-messages="vErrors.collect('recommending')"
								v-validate="'required'"
								:items="viewData.selectItems"
								:disabled="configuration.disabled"
								item-text="name"
								item-value="value"
								v-model="evaluationData.recommending"
								label="Rekomenduję do dofinansowania *"
								aria-label="recommendation"
								for="agency-project-substantive-evaluation-form-recommending"
								id="agency-project-substantive-evaluation-form-recommending"
							></v-select>
						</v-flex>
					</v-layout>
				</v-container>
				<div class="d-none print-only ml-3 header-card-font-size">
					Rekomenduję do dofinansowania: <strong
						class="ml-2 header-card-font-size">{{substantiveRecommendation}}</strong>
				</div>
			</div>
		</v-form>

		<div v-if="evaluationData.recommending || !configuration.booleanEvaluation">
			<div class="no-print">
				<hr class="ma-4">
				<h3 class="mt-4 mb-4 ml-4">Rekomendacja zmiany kwoty</h3>
				<div>
					<v-layout row align-center>
						<v-flex xs5>
							<v-subheader>{{evaluationData.recommendationFoundingSet ?
								"W kwocie zmienionej" :
								"W kwocie wnioskowanej (" + formatMoney(viewData.mszFundings) + " " + viewData.currencyCode
								+ ")"}}
							</v-subheader>
						</v-flex>
						<v-switch
							class="no-print"
							:disabled="configuration.disabled"
							v-model="evaluationData.recommendationFoundingSet"
							aria-label="label for switch"
						></v-switch>
					</v-layout>
					<v-expand-transition>
						<div v-if="evaluationData.recommendationFoundingSet">
							<v-layout row align-center>
								<v-flex xs5>
									<div class="mb-2">
										<v-subheader>Rekomendowana kwota ({{viewData.currencyCode}})</v-subheader>
									</div>
								</v-flex>
								<v-flex xs5>
									<egranty-number-input-field
										data-vv-name="recommendationFounding"
										data-vv-as="Rekomendowana kwota"
										:error-messages="vErrors.collect('recommendationFounding')"
										v-validate="'required'"
										data-vv-validate-on="none"
										name="recommendationFounding"
										label="Rekomendowana kwota"
										for="agency-project-substantive-evaluation-form-recommendation-founding"
										id="agency-project-substantive-evaluation-form-recommendation-founding"
										:disabled="configuration.disabled"
										type="number"
										:suffix="' ' + viewData.currencyCode"
										v-model="evaluationData.recommendationFounding"
										aria-label="label for text"
									/>
								</v-flex>
							</v-layout>
							<v-layout style="page-break-inside: avoid">
								<v-textarea
									browser-autocomplete="off"
									outline
									auto-grow
									data-vv-name="foundingExplanation"
									data-vv-as="Uzasadnienie zmiany kwoty"
									label="Uzasadnienie zmiany kwoty"
									class="funding-explanation-area"
									v-model="evaluationData.foundingExplanation"
									:error-messages="vErrors.collect('foundingExplanation')"
									v-validate="'required|min:10'"
									:disabled="configuration.disabled"
									aria-label="label for text">
								</v-textarea>
							</v-layout>
						</div>
					</v-expand-transition>
				</div>
			</div>
			<div class="d-none print-only">
				<hr class="ma-4">
				<h3 class="mt-4 mb-4 ml-4">Rekomendacja zmiany kwoty</h3>
				<div>
					<div class="mt-3 ml-3 header-card-font-size" v-if="evaluationData.recommendationFoundingSet">W
						kwocie
						zmienionej
					</div>
					<div class="mt-3 ml-3 header-card-font-size" v-else>
						W kwocie wnioskowanej: <strong class="ml-2 header-card-font-size">{{formatMoney(viewData.mszFundings)
							+ " " + viewData.currencyCode}}</strong>
					</div>
					<div class="mt-3 ml-3 header-card-font-size" v-if="evaluationData.recommendationFoundingSet">
						Rekomendowana kwota: <strong class="ml-2 header-card-font-size">
							{{formatMoney(evaluationData.recommendationFounding) + " " + viewData.currencyCode}}</strong>
						<v-layout style="page-break-inside: avoid">
							<div class="mt-3 header-card-font-size">Uzasadnienie zmiany kwoty:
								<div class="div-for-print mt-3 header-card-font-size"
									style="color: black; width: 89vw">{{evaluationData.foundingExplanation}}
								</div>
							</div>
						</v-layout>
					</div>
				</div>
			</div>
		</div>

		<v-btn @click="validateAndShowDialog" v-if="!configuration.disabled" color="success"
			class="no-print">
			<v-icon class="mr-2 white--text">send</v-icon>
			Wyślij
		</v-btn>

		<v-btn @click="saveEvaluation" v-if="!configuration.disabled" color="info" class="no-print">
			<v-icon class="mr-2 white--text">save</v-icon>
			Zapisz opinię
		</v-btn>

		<evaluation-confirmation-dialog :action="send" :dialog-model="viewData.sendEvaluationDialog"
			@close-confirmation="viewData.sendEvaluationDialog = false"
			action-label="Wyślij" content="Upewnij się, że wypełniłeś poprawnie formularz zanim zatwierdzisz opinię.
				Po zatwierdzeniu nie będzie możliwości edycji!" header="Wysyłka opinii" icon="send"/>
	</v-container>
</template>

<script>
	import {Api} from "@/api";
	import {genericFormUtil} from "@/utils/genericFormUtil";
	import Vue from "vue";
	import VueFormGenerator from "vue-form-generator";
	import {AgencySubstantiveEvaluation} from "@/api/generated/AgencySubstantiveEvaluation";
	import EvaluationConfirmationDialog from "../../EvaluationConfirmationDialog";
	import EgrantyNumberInputField from "../../../../../genericComponents/EgrantyNumberInputField";
	import {Numbers} from "@/utils/Numbers";
	import {AgencyContest} from "@/api/generated/AgencyContest";
	import {Files} from "@/utils/Files";

	Vue.use(VueFormGenerator);

	export default {
		name: "AgencyProjectSubstantiveEvaluation",
		components: {EgrantyNumberInputField, EvaluationConfirmationDialog},
		watch: {
			"evaluationData.model": {
				handler () {
					this.applyGenericForm(this.configuration.form);
				},
				deep: true,
			},
		},

		computed: {
			sumOfPoints () {
				let sum = 0;
				this.configuration.parameters.forEach(parameter => {
					if (parameter.value) {
            let part = parameter.value * parameter.multiplier;
						sum = sum + parseFloat(part.toFixed(2));
					}
				});
				return sum;
			},

			recommendationFrameStyle () {
				let color = this.evaluationData.recommending === true ? 'palegreen' :
					this.evaluationData.recommending === false ? 'palevioletred' : 'lightgray';
				return "border:2px solid " + color + "; border-radius: 25px;";
			},

			substantiveRecommendation () {
				let found = this.viewData.selectItems.find(item => item.value === this.evaluationData.recommending);
				if (!found) return "";
				return found.name;
			}
		},

		data () {
			return {
				configuration: {
					booleanEvaluation: false,
					form: "",
					parameters: [],
					disabled: false
				},

				evaluationData: {
					model: undefined,
					points: [],
					recommending: undefined,
					recommendationFoundingSet: false,
					recommendationFounding: 0,
					foundingExplanation: ''
				},

				viewData: {
					showGenerator: false,
					agencyId: '',
					agencyProjectId: '',
					agencyProjectTitle: '',
					agencyProjectCode: '',
					controlSum: '',
					mszFundings: '',
					currencyCode: '',
					country: '',
					author: '',
          firstSendDate: '',
          latestSendDate: '',
					extraCountries: [],
					sendEvaluationDialog: false,
					selectItems: [
						{
							name: "TAK", value: true,
						},
						{
							name: "NIE", value: false,
						},
					],
				},

				formGenerator: {
					currentSchema: {},
					currentModel: {},
					formOptions: {
						validateAfterLoad: false,
						validateAfterChanged: true,
						validateAsync: true,
					},
					currentConditionalHashes: []
				},

				contestData: {
					id: this.$route.params.id,
					evaluationOpen: undefined,
					evaluationFinished: undefined
				},

				validation: {},
			};
		},

		created () {
			this.loadData();
		},

		methods: {

			async loadData () {
				await Api.syncGet(this, "/agency-substantive-evaluation/configuration?contestId=".concat(this.$route.params.id), successResponse => {
					this.configuration.booleanEvaluation = successResponse.booleanEvaluation;
					this.configuration.form = genericFormUtil.decodeSchema(successResponse.form);
					this.configuration.parameters = successResponse.parameters;
				});

				AgencySubstantiveEvaluation.get(this, this.$route.params.evaluationId, success => {
					if (success.filledForm === null) {
						this.evaluationData.model = genericFormUtil.buildModelFromSchema(this.configuration.form).sections[0].fields;
					} else {
						this.evaluationData.model = genericFormUtil.decodeSchema(success.filledForm);
					}
					this.viewData.agencyId = success.agencyId;
					this.viewData.agencyProjectId = success.agencyProjectId;
					this.viewData.agencyProjectTitle = success.title;
					this.viewData.agencyProjectCode = success.agencyProjectCode;
					this.viewData.controlSum = success.controlSum;
					this.viewData.mszFundings = success.mszFundings;
					this.viewData.currencyCode = success.currencyCode;
					this.viewData.author = success.nameSurname;
					this.viewData.country = success.country;
          this.viewData.firstSendDate = success.firstSendDate;
          this.viewData.latestSendDate = success.latestSendDate;
					this.viewData.agencyName = success.agencyName;
					this.viewData.extraCountries = [];
					success.extraCountries.forEach(country => {
						this.viewData.extraCountries.push(country);
					});
					this.evaluationData.extraCountries = success;

					if (success.evaluationStatus === "FINISHED" || !success.authorIdentity) {
						this.configuration.disabled = true;
					}
					this.applyGenericForm(this.configuration.form);
					this.evaluationData.points = success.substantiveEvaluationPoints;
					this.evaluationData.recommendationFoundingSet = success.recommendationFoundingSet;
					this.evaluationData.recommendationFounding = success.recommendationFounding;
					this.evaluationData.foundingExplanation = success.foundingExplanation;
					this.evaluationData.recommending = success.evaluationSet ? success.recommending : undefined;
					this.viewData.showGenerator = true;
					this.addValueToRatings();
				});

				AgencyContest.getState(this, this.contestData.id,
					response => {
						this.contestData.evaluationOpen = response.agencyContestStatus === 'STARTED';
						this.contestData.evaluationFinished = response.agencyContestStatus === 'FINISHED';
					});
			},

			addValueToRatings () {
				for (let i = 0; i < this.configuration.parameters.length; i++) {
					let parameterId = this.configuration.parameters[i].id;
					let point = this.evaluationData.points.find(point => {
						return point.id.ratingParameterId === parameterId;
					});
					if (point && point.points) {
						this.$set(this.configuration.parameters[i], "value", parseInt(point.points, 10));
					} else {
						this.$set(this.configuration.parameters[i], "value", 0);
					}
				}
			},

			saveEvaluation () {
				AgencySubstantiveEvaluation.putData(this, this.$route.params.evaluationId, this.prepareFormData(), successResponse => {
					this.$messagesBus.$emit("success", "Pomyślnie zapisano opinię", successResponse);
					this.loadData();
				}, errorResponse => {
					this.$messagesBus.$emit("error", "Nie udało się zapisać opinii", errorResponse);
				});
			},

			async send () {
				let validationPromise = await this.$refs.vfg.validate();
				if (validationPromise.length > 0) {
					this.$messagesBus.$emit("error", "Uzupełnij poprawnie dane formularza");
					return;
				}
				this.$validator.validateAll().then((result) => {
					if (result) {
						AgencySubstantiveEvaluation.putFinish(this, this.$route.params.evaluationId, this.prepareFormData(), successResponse => {
							this.$messagesBus.$emit("success", "Opinia została wysłana", successResponse);
							this.$router.push("/app/agency-evaluations/substantive");
						}, errorResponse => {
							this.viewData.sendEvaluationDialog = false;
							this.$messagesBus.$emit("error", "Nie udało się wysłać opinii", errorResponse);
						});
					} else {
						this.viewData.sendEvaluationDialog = false;
						this.$messagesBus.$emit("error", this.foundingNotFilled() ?
							"Podaj kwotę rekomendowaną" : "Uzupełnij poprawnie dane opinii");
					}
				});
			},

			prepareFormData () {
				let points = {};
				this.configuration.parameters.forEach(parameter => {
					points[parseInt(parameter.id)] = parameter.value;
				});
				return {
					evaluationSet: this.evaluationData.recommending !== undefined,
					recommending: this.evaluationData.recommending === undefined ? false : this.evaluationData.recommending,
					recommendationFoundingSet: this.evaluationData.recommendationFoundingSet,
					recommendationFounding: this.evaluationData.recommendationFounding,
					foundingExplanation: this.evaluationData.foundingExplanation,
					filledForm: genericFormUtil.encodeSchema(this.evaluationData.model),
					points: points,
				};
			},

			applyGenericForm (form) {
				let schema = genericFormUtil.getSectionSchemaFromSchema(form, 0, this.configuration.disabled);
				if (genericFormUtil.sectionSchemaContainsConditionalField(schema)) {
					let schemaValues = this.evaluationData.model;
					this.formGenerator.currentSchema = genericFormUtil.filterSchemaByConditionalFieldsValue(schema, schemaValues);
					let indexes = genericFormUtil.calculateIndexes(schema);
					this.formGenerator.currentConditionalHashes = genericFormUtil.getConditionalValueHashes(schema, indexes);
				} else {
					this.formGenerator.currentSchema = schema;
				}
			},

			async validateAndShowDialog () {
				let validationPromise = await this.$refs.vfg.validate();
				if (validationPromise.length > 0) {
					this.$messagesBus.$emit("error", "Uzupełnij poprawnie dane formularza");
					return;
				}
				if (this.contestData.evaluationFinished) {
					this.$messagesBus.$emit("error", "Ocena w naborze została już zakończona");
					return;
				}
				this.$validator.validateAll().then((result) => {
					if (!result) {
						this.$messagesBus.$emit("error", this.foundingNotFilled() ?
							"Podaj kwotę rekomendowaną" : "Uzupełnij poprawnie dane opinii");
					} else {
						this.viewData.sendEvaluationDialog = true;
					}
				});
			},

			foundingNotFilled () {
				return this.evaluationData.recommending &&
					this.evaluationData.recommendationFounding === null ||
					this.evaluationData.recommendationFounding === "";
			},

      openProjectView() {
        this.$router.openInNewTab('/view/agency-contest/' + this.$route.params.id + '/project/' + this.viewData.agencyId + '/' + this.viewData.agencyProjectId);
      },

			async print () {
				await Files.methods.downloadFileAsBlob(
					this,
					Files.methods.getAgencyEvaluationPrintUrl(
						this.$route.params.id,
						"substantive",
						this.$route.params.evaluationId
					),
					"opinia_indywidualna.pdf"
				);
			},
		},

		mixins: [
			Numbers
		]
	};
</script>

<style>
	.header-card-font-size {
		font-size: 14px;
	}

	@media print {
		.header-card-font-size {
			font-size: 13px;
		}

		.evaluation-font-size * {
			font-size: 13px;
		}
	}
</style>
